import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PatientsHeader } from "../components/PatientsHeader";
import { PatientsTable } from "../components/PatientsTable";
import { PatientsPagination } from "../components/PatientsPagination";
import { AddPatientDialog } from "../components/AddPatientDialog";
import { useGetPatientsQuery } from "../../infrastructure/api/patientsApi";
import { usePatientPermissions } from "../../application/hooks/usePatientPermissions";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Patient } from "../../domain/entities/Patient";
import { PageShell } from "@/components/layout/PageShell";

type SortField = "created_at" | "name";
type SortOrder = "asc" | "desc";
type FilterType = "All" | "Male" | "Female";
type SortFilterKey = "newest" | "oldest" | "nameAsc" | "nameDesc";

const ITEMS_PER_PAGE = 10;

const Patients: React.FC = () => {
  const { t } = useTranslation("patients");
  const { can } = usePatientPermissions();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState<FilterType>("All");
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const navigate = useNavigate();

  const queryParams = {
    query: searchQuery || undefined,
    page: currentPage,
    page_size: ITEMS_PER_PAGE,
    sort_by: sortField,
    sort_order: sortOrder,
  };

  const {
    data: patientsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetPatientsQuery(queryParams);

  useEffect(() => {
    if (error) {
      toast.error(t("loadError"));
    }
  }, [error, t]);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterType]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleView = (patient: Patient) => {
    navigate(`/patients/${patient.id}`);
  };

  const handleAddSuccess = () => {
    refetch();
    setAddDialogOpen(false);
  };

  const handleFilterChange = (filter: string) => {
    if (filter === "newest") {
      setSortField("created_at");
      setSortOrder("desc");
      setFilterType("All");
    } else if (filter === "oldest") {
      setSortField("created_at");
      setSortOrder("asc");
      setFilterType("All");
    } else if (filter === "nameAsc") {
      setSortField("name");
      setSortOrder("asc");
      setFilterType("All");
    } else if (filter === "nameDesc") {
      setSortField("name");
      setSortOrder("desc");
      setFilterType("All");
    } else if (filter === "Male") {
      setFilterType("Male");
    } else if (filter === "Female") {
      setFilterType("Female");
    } else {
      setFilterType("All");
    }
  };

  const activeFilter: SortFilterKey =
    sortField === "created_at" && sortOrder === "desc"
      ? "newest"
      : sortField === "created_at" && sortOrder === "asc"
        ? "oldest"
        : sortField === "name" && sortOrder === "asc"
          ? "nameAsc"
          : "nameDesc";

  const patients = patientsData?.patients;
  const filteredPatients = React.useMemo(() => {
    if (!patients) return [];
    if (filterType === "All") return patients;
    return patients.filter(
      (p) => p.gender.toLowerCase() === filterType.toLowerCase(),
    );
  }, [patients, filterType]);

  return (
    <PageShell className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0">
        <PatientsHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onAddPatient={() => setAddDialogOpen(true)}
          onFilterChange={handleFilterChange}
          activeFilter={activeFilter}
          canCreate={can("patient_create")}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            <PatientsTable
              patients={filteredPatients}
              onView={handleView}
              isLoading={isFetching}
              canView={can("patient_read")}
            />
            {filteredPatients.length > 0 && (
              <PatientsPagination
                currentPage={currentPage}
                totalPages={patientsData?.meta.total_pages ?? 1}
                totalItems={patientsData?.meta.total_count ?? 0}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      <AddPatientDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSuccess={handleAddSuccess}
      />
    </PageShell>
  );
};

export default Patients;
